/**
 * Lightweight YouTube video card.
 * Opens the canonical YouTube watch page directly because some recordings are
 * blocked or bot-gated inside embedded iframes in preview/sandbox browsers.
 */
export function YouTubeFacade({
  videoId,
  title,
  watchUrl,
  className = "",
}: {
  videoId: string;
  title: string;
  watchUrl?: string;
  className?: string;
}) {
  const thumb = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
  const href = watchUrl ?? `https://www.youtube.com/watch?v=${videoId}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`YouTube'da aç: ${title}`}
      className={`group relative block aspect-video w-full overflow-hidden bg-ink/10 cursor-pointer ${className}`}
    >
      <img
        src={thumb}
        alt={title}
        loading="lazy"
        decoding="async"
        width={1280}
        height={720}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        onError={(e) => {
          // maxresdefault occasionally 404s; fall back to hqdefault
          const img = e.currentTarget;
          if (!img.dataset.fallback) {
            img.dataset.fallback = "1";
            img.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
          }
        }}
      />
      <span
        aria-hidden
        className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25"
      >
        <span className="flex h-16 w-24 items-center justify-center rounded-xl bg-black/80 text-paper shadow-lg transition-transform group-hover:scale-105">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>
    </a>
  );
}
